import { Transition } from "@headlessui/react";
import { createContext, ReactNode, useState } from "react";

import SystemMessage, {
  MessageType,
} from "@/components/molecules/SystemMessage";

interface Message {
  type: MessageType;
  text: string;
}

export interface PushMessageOption {
  timeout?: number;
}

export type SystemMessageContextType = {
  message?: Message;
  push: (message: Message, option?: PushMessageOption) => void;
};

export const SystemMessageContext = createContext<SystemMessageContextType>({
  message: undefined,
  push: () => {
    throw new Error("Not implemented");
  },
});

interface Props {
  children: ReactNode;
}

export function SystemMessageProvider({ children }: Props) {
  const [message, setMessage] = useState<undefined | Message>(undefined);
  const [timeoutId, setTimeoutId] = useState<undefined | number>(undefined);
  const [isShowing, setIsShowing] = useState(true);
  const push = (message: Message, option?: PushMessageOption) => {
    const { timeout = 3000 } = option ?? {};

    setMessage(message);
    setIsShowing(true);

    const id = window.setTimeout(() => {
      setIsShowing(false);
    }, timeout);
    setTimeoutId(id);
  };

  return (
    <SystemMessageContext.Provider value={{ message, push }}>
      {message && (
        <Transition
          appear
          className="fixed top-20 left-0 z-50 w-full text-center"
          show={isShowing}
          enter="transition-all duration-300"
          enterFrom="opacity-0 -translate-y-5"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-400"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-90"
          afterLeave={() => {
            setMessage(() => undefined);
            window.clearTimeout(timeoutId);
          }}
        >
          <SystemMessage type={message.type}>{message.text}</SystemMessage>
        </Transition>
      )}
      {children}
    </SystemMessageContext.Provider>
  );
}
